import { reportError } from './telemetry'

/** Le script produit par vite-plugin-pwa, servi a la racine. */
const SCRIPT = '/sw.js'

/**
 * Enregistre le service worker, et donne un nom à son échec — quand cet
 * échec nous regarde.
 *
 * Le script injecté par vite-plugin-pwa appelait
 * `navigator.serviceWorker.register()` sans rien rattacher au rejet. Quand
 * l'enregistrement échouait, la promesse partait en `unhandledrejection`,
 * et le seul message qui arrivait dans les journaux du serveur était
 * « Rejected » : trois rapports de vrais clients, sur trois navigateurs
 * différents, sans la moindre indication de ce qui avait échoué. Il a fallu
 * lire la pile d'appels sur le serveur pour découvrir de quoi il s'agissait.
 *
 * Un échec n'est pas une panne pour le client : il perd le cache hors
 * ligne, le site fonctionne. On ne lui montre donc rien.
 *
 * @param {() => Promise<{registerSW: Function}>} charger  le module
 *        d'enregistrement. Injectable pour que le test n'ait pas besoin de
 *        résoudre un module virtuel.
 */
export function enregistrerLeServiceWorker(charger = () => import('virtual:pwa-register')) {
  // Un navigateur qui ne connaît pas les service workers n'a rien à dire.
  if (typeof navigator === 'undefined' || ! ('serviceWorker' in navigator)) {
    return Promise.resolve()
  }

  return charger()
    .then(({ registerSW }) => registerSW({
      immediate: true,
      onRegisterError: (erreur) => signaler(erreur),
    }))
    /*
     | Deux chemins d'échec distincts, et il faut les deux : onRegisterError
     | couvre le refus du navigateur, ce .catch couvre le cas où le module
     | lui-même ne se charge pas — un chunk absent après un déploiement, par
     | exemple. Sans lui, on aurait remplacé un rejet non rattrapé par un
     | autre.
     */
    .catch((erreur) => signaler(erreur))
}

/**
 * Ne prévient que si le script est en cause.
 *
 * Un enregistrement échoue pour deux familles de raisons, et une seule nous
 * concerne. Côté client : navigation privée, stockage refusé, service
 * workers désactivés par une politique d'entreprise. C'est le choix de
 * l'utilisateur ou de son administrateur, ça ne se corrige pas, et le
 * remonter enverrait un rapport à chaque visite d'un navigateur en mode
 * privé — une boutique en produirait des centaines par jour, et la
 * surveillance redeviendrait le bruit qu'on a passé des semaines à
 * éteindre. Côté serveur : sw.js qui répond 404 ou avec un type MIME que le
 * navigateur refuse, après un déploiement incomplet. Celui-là, il faut le
 * voir — c'est exactement ce que disait le seul des trois rapports qui
 * portait un message lisible.
 *
 * On ne devine pas laquelle des deux : on va chercher le script et on
 * regarde. Une requête, seulement en cas d'échec.
 */
async function signaler(erreur) {
  if (await leScriptEstServi()) {
    return
  }

  reportError(
    erreur instanceof Error ? erreur : new Error(String(erreur ?? 'Enregistrement refuse')),
    { source: 'service-worker' },
  )
}

async function leScriptEstServi() {
  try {
    const reponse = await fetch(SCRIPT, { cache: 'no-store' })

    return reponse.ok && /javascript/i.test(reponse.headers.get('content-type') ?? '')
  } catch {
    // Injoignable : c'est bien le script qui manque, pas le navigateur qui
    // refuse. On prévient.
    return false
  }
}
